import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Slide,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Today,
  DateRange,
  CalendarMonth,
  Close,
  Check,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import moment from "moment";
import "moment/locale/fr";

// Configuration française
moment.locale("fr");

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MOIS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

export default function DatePickerDialog(props) {
  const {
    open,
    onClose,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setGroupedBy,
  } = props;

  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd] = useState(null);
  const [selectingEnd, setSelectingEnd] = useState(false);

  // Sync with props when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedStart(moment(startDate));
      setSelectedEnd(moment(endDate));
      setCurrentMonth(moment(startDate));
      setSelectingEnd(false);
    }
  }, [open, startDate, endDate]);

  // Générer les jours du mois
  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");

    let startDay = startOfMonth.clone();
    while (startDay.day() !== 1) {
      startDay.subtract(1, "day");
    }

    let endDay = endOfMonth.clone();
    while (endDay.day() !== 0) {
      endDay.add(1, "day");
    }

    const days = [];
    const current = startDay.clone();
    while (current.isSameOrBefore(endDay)) {
      days.push(current.clone());
      current.add(1, "day");
    }

    return days;
  };

  const handleDayClick = (day) => {
    if (!selectingEnd) {
      setSelectedStart(day.clone().startOf("day"));
      setSelectedEnd(day.clone().endOf("day"));
      setSelectingEnd(true);
    } else {
      if (day.isBefore(selectedStart)) {
        setSelectedEnd(selectedStart.clone().endOf("day"));
        setSelectedStart(day.clone().startOf("day"));
      } else {
        setSelectedEnd(day.clone().endOf("day"));
      }
      setSelectingEnd(false);
    }
  };

  const isSelected = (day) => {
    if (!selectedStart) return false;
    return day.isSame(selectedStart, "day") || day.isSame(selectedEnd, "day");
  };

  const isInRange = (day) => {
    if (!selectedStart || !selectedEnd) return false;
    return day.isBetween(selectedStart, selectedEnd, "day", "[]");
  };

  const isToday = (day) => day.isSame(moment(), "day");
  const isCurrentMonth = (day) => day.month() === currentMonth.month();

  const presets = [
    {
      label: "Aujourd'hui",
      icon: <Today sx={{ fontSize: 16 }} />,
      action: () => {
        const today = moment();
        setSelectedStart(today.clone().startOf("day"));
        setSelectedEnd(today.clone().endOf("day"));
        setSelectingEnd(false);
      }
    },
    {
      label: "Cette semaine",
      icon: <DateRange sx={{ fontSize: 16 }} />,
      action: () => {
        setSelectedStart(moment().startOf("isoWeek"));
        setSelectedEnd(moment().endOf("isoWeek"));
        setSelectingEnd(false);
      }
    },
    {
      label: "Ce mois",
      icon: <CalendarMonth sx={{ fontSize: 16 }} />,
      action: () => {
        setSelectedStart(moment().startOf("month"));
        setSelectedEnd(moment().endOf("month"));
        setSelectingEnd(false);
      }
    },
  ];

  const handleApply = () => {
    if (!selectedStart || !selectedEnd) return;

    const diff = Math.abs(selectedEnd.diff(selectedStart, "hours"));
    if (diff > 25) {
      setGroupedBy("daily");
    } else {
      setGroupedBy("hourly");
    }
    setStartDate(selectedStart);
    setEndDate(selectedEnd);
    onClose();
  };

  const days = generateCalendarDays();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: "linear-gradient(145deg, #1e2724 0%, #151a17 100%)",
          border: "1px solid rgba(38, 166, 154, 0.2)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(38, 166, 154, 0.08)",
      }}>
        <Typography sx={{
          fontSize: "1.1rem",
          fontWeight: 600,
          color: "#fff",
        }}>
          Sélectionner une période
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.6)" }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Raccourcis rapides */}
        <Box sx={{
          display: "flex",
          gap: 1,
          p: 2,
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          flexWrap: "wrap",
          justifyContent: "center",
        }}>
          {presets.map((preset, idx) => (
            <Chip
              key={idx}
              icon={preset.icon}
              label={preset.label}
              onClick={preset.action}
              size="small"
              sx={{
                backgroundColor: "rgba(38, 166, 154, 0.15)",
                color: "#26A69A",
                border: "1px solid rgba(38, 166, 154, 0.3)",
                fontWeight: 500,
                fontSize: "0.75rem",
                transition: "all 0.2s ease",
                "& .MuiChip-icon": { color: "#26A69A" },
                "&:hover": {
                  backgroundColor: "rgba(38, 166, 154, 0.3)",
                  transform: "translateY(-1px)",
                },
              }}
            />
          ))}
        </Box>

        {/* Navigation du mois */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}>
          <IconButton
            onClick={() => setCurrentMonth(m => m.clone().subtract(1, "month"))}
            sx={{
              color: "#26A69A",
              "&:hover": { backgroundColor: "rgba(38, 166, 154, 0.1)" }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography sx={{
            fontWeight: 600,
            color: "#fff",
            fontSize: "1rem",
          }}>
            {MOIS[currentMonth.month()]} {currentMonth.year()}
          </Typography>
          <IconButton
            onClick={() => setCurrentMonth(m => m.clone().add(1, "month"))}
            sx={{
              color: "#26A69A",
              "&:hover": { backgroundColor: "rgba(38, 166, 154, 0.1)" }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Jours de la semaine */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          px: 1.5,
          pb: 1,
        }}>
          {JOURS.map((jour) => (
            <Typography
              key={jour}
              sx={{
                textAlign: "center",
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.4)",
                textTransform: "uppercase",
              }}
            >
              {jour}
            </Typography>
          ))}
        </Box>

        {/* Grille des jours */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
          px: 1.5,
          pb: 2,
        }}>
          {days.map((day, idx) => {
            const selected = isSelected(day);
            const inRange = isInRange(day);
            const today = isToday(day);
            const currentMo = isCurrentMonth(day);

            return (
              <Box
                key={idx}
                onClick={() => handleDayClick(day)}
                sx={{
                  aspectRatio: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: selected ? "50%" : inRange ? "4px" : "50%",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  position: "relative",
                  backgroundColor: selected
                    ? "#26A69A"
                    : inRange
                      ? "rgba(38, 166, 154, 0.2)"
                      : "transparent",
                  border: today && !selected
                    ? "2px solid #26A69A"
                    : "2px solid transparent",
                  "&:hover": {
                    backgroundColor: selected
                      ? "#26A69A"
                      : "rgba(38, 166, 154, 0.3)",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <Typography sx={{
                  fontSize: "0.85rem",
                  fontWeight: selected || today ? 600 : 400,
                  color: selected
                    ? "#000"
                    : currentMo
                      ? "#fff"
                      : "rgba(255,255,255,0.25)",
                }}>
                  {day.date()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Période sélectionnée */}
        <Box sx={{
          mx: 2,
          mb: 2,
          p: 1.5,
          borderRadius: "10px",
          background: "rgba(38, 166, 154, 0.1)",
          border: "1px solid rgba(38, 166, 154, 0.2)",
        }}>
          <Typography sx={{
            textAlign: "center",
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.6)",
            mb: 0.5,
          }}>
            {selectingEnd ? "Sélectionnez la date de fin" : "Période sélectionnée"}
          </Typography>
          <Typography sx={{
            textAlign: "center",
            fontWeight: 600,
            color: "#26A69A",
            fontSize: "1rem",
          }}>
            {selectedStart && selectedEnd
              ? selectedStart.isSame(selectedEnd, "day")
                ? selectedStart.format("dddd D MMMM YYYY")
                : `${selectedStart.format("D MMM")} → ${selectedEnd.format("D MMM YYYY")}`
              : "—"
            }
          </Typography>
        </Box>

        {/* Boutons d'action */}
        <Box sx={{
          display: "flex",
          gap: 1.5,
          p: 2,
          pt: 0,
        }}>
          <Button
            onClick={onClose}
            fullWidth
            sx={{
              py: 1.2,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.05)",
              color: "rgba(255,255,255,0.7)",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9rem",
              border: "1px solid rgba(255,255,255,0.1)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleApply}
            fullWidth
            startIcon={<Check />}
            sx={{
              py: 1.2,
              borderRadius: "10px",
              backgroundColor: "#26A69A",
              color: "#000",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9rem",
              "&:hover": {
                backgroundColor: "#2bbd9e",
                boxShadow: "0 4px 20px rgba(38, 166, 154, 0.4)",
              },
            }}
          >
            Appliquer
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
